import { Component, Mixins, Prop } from 'vue-property-decorator';
import { COURSE_STORAGE_KEYS } from '@/courses/values/courses.values';
import { PROJECT_COMMONS, SECTION_TYPES } from '@/content/values/sections';
import { COURSE_EVENTS } from '@/content/values/events';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import { COMPONENT_TYPE } from '@/courses/values/courses.values'
import { IparamsComponentSelected } from '@/courses/interfaces/params-component-selected'
import { CourseToolsMixin } from './course-tools.mixin';
import KEYS from '@/values/keys';
import { LocalStorageService } from '@/services/local-storage.service';
import { SectionsProvider } from '@/content/providers/sections.provider';
import { Section } from '../models/section.model';
import Course from '@/content/models/course.model'
import { EdSectionOptions } from '@/content/models/ed-section-options.model';
import { Project } from '../models/project.model';

@Component({})
export class ProjectsMixin extends Mixins(CourseToolsMixin) {

  /* Props */
  @Prop({default: null}) readonly project: Project;
  @Prop({default: null}) readonly options: EdSectionOptions;
  @Prop({default: null}) readonly courseId: number;
  @Prop({default: null}) readonly classId: number;

  /* Data */
  public emptyDiv: any = null;

  /* computed */
  get courseID(): number {
    return this.project ? this.project.contentId : this.courseId;
  }

  get classID(): number {
    return this.project ? this.project.classId : this.classId;
  }

  get course(): Course {
    return this.getCourseFromStore({courseId: this.courseID , classId: this.classID});
  }

  get isTeacher(): Boolean {
    return this.$store.state.AuthStore.role == KEYS.ROLES.PUBLISHER;
  }

  /* Methods */
  goToCreateProject() {
    let selectInfo: IparamsComponentSelected = {
      courseId: this.courseID,
      classId: this.classID, 
      componentInfo: { 
        name: COMPONENT_TYPE.PROJECT_CREATOR.code,
        section: this.project,
        edition: false
      }
    };
    this.goToComponentSelected(selectInfo);
  }

  goToEditProject() {
    let selectInfo: IparamsComponentSelected = {
      courseId: this.courseID,
      classId: this.classID, 
      componentInfo: { 
        name: COMPONENT_TYPE.PROJECT_CREATOR.code,
        section: this.project,
        edition: true
      }
    };
    this.goToComponentSelected(selectInfo);
  }
  
  moveProject(e) {
    let result = true;
    this.$Courses.Store.setMovingObject(true);
    
    /* Check if the element is above an empty section */
    if(e.to.className == 'parent-empty') {
      e.to.children[0].classList.add('edg-d--none');
      this.emptyDiv = e.to.children[0];
    } else {
      if(this.emptyDiv) {
        this.emptyDiv.classList.remove('edg-d--none');
        this.emptyDiv = null;
      }
    }
    
    if(e.related.childNodes[0].className.indexOf('ed-section-projects--not-opened') !== -1) {      
      this.$edEventBus.$emit(COURSE_EVENTS.PROJECT_OPENED, { opened: true, projectId: e.related.childNodes[0].id });
    }

    /* Check if the element is a parent section then close that */
    if(e.draggedContext.element.type === SECTION_TYPES.PROJECT.code) {
      this.$edEventBus.$emit(COURSE_EVENTS.PROJECT_OPENED, {opened: false, projectId: e.draggedContext.element.id});
    }
    
    /* Check if the user try to put an object in the main tree */
    if(e.to.className == PROJECT_COMMONS.PROJECT_PARENT_LIST) {
      result = e.draggedContext.element.type === SECTION_TYPES.PROJECT.code;
    }
    
    return result;

  }

  getTypeFromExtension(extension: string = '') {
    let keys = Object.keys(SECTION_TYPES);
    let type = '';
    keys.forEach((key) => {
      let extensions = SECTION_TYPES[key].extensions;
      let extFound = extensions.find(ext => ext == extension.toUpperCase());
      if(extFound && SECTION_TYPES[key].groupName == 'file') {
        type = SECTION_TYPES[key].code;
      }
    });
    return type;
  }

  isFileExtensionValid(filename: string) {
    let file = filename.split('.');
    let ext = file[file.length - 1].toLowerCase();
    let exts = Object.keys(KEYS.FILES_TYPE).map(e => e.toLowerCase());
    let found = exts.findIndex(e => e == ext);
    return found !== -1;
  }

  setFolderInStorage(folderId: number, parentId?: number) {
    let state = LocalStorageService.getItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_FOLDERS_STATE) || {};
    if(state instanceof Array){
      state = {};
    }
    let folders: number[] = state[this.courseID] ? state[this.courseID] : [];
    if(folders.includes(folderId)) {
      let i = folders.findIndex(f => f == folderId);
      folders.splice(i, folders.length - i);
      this.setSectionInStorage(null);
    } else if(parentId) {
      folders.push(folderId);
    } else {
      folders = [folderId];
      this.setSectionInStorage(null);
    }
    state[this.courseID] = folders;
    LocalStorageService.setItem(
      COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_FOLDERS_STATE,
      state
    );
  }

  setSectionInStorage(sectionId: number) {
    let currentSectionState = LocalStorageService.getItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_SECTION_STATE) || {};
    let currentMaterialState = LocalStorageService.getItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_MATERIAL_STATE) || {};
    let currentProjectState = LocalStorageService.getItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_PROJECT_STATE) || {};
    
    if(typeof currentSectionState === 'number'){
      currentSectionState = {};
    }

    if(sectionId) {
      currentProjectState = {...currentSectionState, [this.courseID] : sectionId};
      delete currentMaterialState[this.courseID];
      delete currentSectionState[this.courseID];
    } else {
      delete currentProjectState[this.courseID];
    }

    LocalStorageService.setItem(
      COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_SECTION_STATE, currentSectionState
    );
    LocalStorageService.setItem(
      COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_MATERIAL_STATE, currentMaterialState
    );
    LocalStorageService.setItem(
      COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_PROJECT_STATE, currentProjectState
    );
  }

  /* Deprecated */
  setTeacherOnlySection(courseId: number, sectionId: number , classId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      let section = SectionsProvider.getSectionByID(
        this.course.sections,
        sectionId
      );
      if(!section) {
        resolve();
      }
      let teacherOnly = !section.teacherOnly;
      this.$Courses.Store.setTeacherOnlySectionA({
        courseId, sectionId, teacherOnly, classId
      })
      .then(() => {
        resolve();
      }).catch((err) => {
        reject(err);
      });  
    });
  }

  async checkParentTeacherOnly(
    courseId: number,
    sections: Section[],
    section: Section,
    parentId: number,
    classId: number) {
    let parent = SectionsProvider.getSectionByID(sections, parentId);
    if(!section.teacherOnly) {
      while(parent.teacherOnly) {
        await this.setTeacherOnlySection(courseId, parent.sectionId, classId);
        if(!parent.parentId) return;
        parent = SectionsProvider.getSectionByID(sections, parent.parentId);
      }
      return;
    } else {
      let teacherOnlySections = parent.sections.filter(section => section.teacherOnly);
      while(parent.sections.length == teacherOnlySections.length) {
        await this.setTeacherOnlySection(courseId, parent.sectionId, classId);
        if(!parent.parentId) return;
        parent = SectionsProvider.getSectionByID(sections, parent.parentId);
      }
      return;
    }
  }

  removeProject({projectId , courseId , classId}: 
    {projectId: number , courseId: number , classId: number}): Promise<void> {
    let data = {projectId, courseId , classId};
    return new Promise((resolve , reject) => {
      this.$Courses.Store.removeProjectA(data)
      .then(() => {
        this.$edEventBus.$emit(COURSES_EVENTS.PROJECT_REMOVED, projectId);
        resolve();
      })
      .catch(() => reject());
    });
  }
}
